import { ref } from 'vue'

export function useAnimations() {
  const animationContainer = ref<HTMLElement | null>(null)

  function createSuccessAnimation() {
    if (!animationContainer.value) return

    // Create success celebration overlay
    const overlay = document.createElement('div')
    overlay.className = 'success-celebration'

    // Create explosion effect
    const explosion = document.createElement('div')
    explosion.className = 'success-explosion'

    // Create particles
    for (let i = 0; i < 20; i++) {
      const particle = document.createElement('div')
      particle.className = 'success-particle'

      // Random direction and distance
      const angle = (i / 20) * Math.PI * 2
      const distance = 100 + Math.random() * 100
      const dx = Math.cos(angle) * distance
      const dy = Math.sin(angle) * distance

      particle.style.setProperty('--dx', `${dx}px`)
      particle.style.setProperty('--dy', `${dy}px`)
      particle.style.animationDelay = `${Math.random() * 0.5}s`

      explosion.appendChild(particle)
    }

    // Create success text
    const successText = document.createElement('div')
    successText.className = 'success-text'
    successText.textContent = '🎉 WELL DONE! 🎉'

    // Create screen flash
    const flash = document.createElement('div')
    flash.className = 'screen-flash-success'

    overlay.appendChild(explosion)
    overlay.appendChild(successText)
    overlay.appendChild(flash)
    document.body.appendChild(overlay)

    // Create floating hearts
    for (let i = 0; i < 5; i++) {
      setTimeout(() => {
        createFloatingHeart()
      }, i * 200)
    }

    // Remove overlay after animation
    setTimeout(() => {
      if (document.body.contains(overlay)) {
        document.body.removeChild(overlay)
      }
    }, 3000)
  }

  function createFailureAnimation(reason: 'timeout' | 'invalid' | null | undefined) {
    if (!animationContainer.value) return

    // Create failure overlay
    const overlay = document.createElement('div')
    overlay.className = 'failure-overlay'

    // Create failure text
    const failureText = document.createElement('div')
    failureText.className = 'failure-text'
    if (reason === 'invalid') {
      // Self-fail / not allowed scenario
      failureText.textContent = '🚫 NOT ALLOWED! 🚫'
    } else if (reason === 'timeout') {
      // Timeout
      failureText.textContent = "💥 TIME'S UP! 💥"
    } else {
      // null / undefined
      failureText.textContent = '❌  FAILED!  ❌'
    }

    // Create crack effect
    const crack = document.createElement('div')
    crack.className = 'failure-crack'

    // Create screen flash
    const flash = document.createElement('div')
    flash.className = 'screen-flash-fail'

    overlay.appendChild(failureText)
    overlay.appendChild(crack)
    document.body.appendChild(overlay)
    document.body.appendChild(flash)

    // Add shake effect to the main container
    const mainContainer =
      document.querySelector('.countdown-page') || document.querySelector('.control-page')
    if (mainContainer) {
      mainContainer.classList.add('failure-shake')
      setTimeout(() => {
        mainContainer.classList.remove('failure-shake')
      }, 2000)
    }

    // Remove overlays after animation
    setTimeout(() => {
      if (document.body.contains(overlay)) {
        document.body.removeChild(overlay)
      }
      if (document.body.contains(flash)) {
        document.body.removeChild(flash)
      }
    }, 3000)
  }

  function createFloatingHeart() {
    const heart = document.createElement('div')
    heart.className = 'floating-heart'
    heart.textContent = '💖'

    // Random horizontal position
    const x = Math.random() * window.innerWidth
    const y = window.innerHeight - 50

    heart.style.left = `${x}px`
    heart.style.top = `${y}px`
    heart.style.animationDelay = `${Math.random() * 0.5}s`

    document.body.appendChild(heart)

    // Remove heart after animation
    setTimeout(() => {
      if (document.body.contains(heart)) {
        document.body.removeChild(heart)
      }
    }, 3000)
  }

  function addSuccessButtonEffect(button: HTMLElement) {
    button.classList.add('success-button')

    const handleClick = () => {
      button.classList.add('clicked')
      setTimeout(() => {
        button.classList.remove('clicked')
      }, 600)
    }

    button.addEventListener('click', handleClick)

    return () => {
      button.removeEventListener('click', handleClick)
      button.classList.remove('success-button')
    }
  }

  function addCountdownTickEffect(element: HTMLElement) {
    element.classList.add('countdown-tick')
    setTimeout(() => {
      element.classList.remove('countdown-tick')
    }, 1000)
  }

  function addUrgentPulse(element: HTMLElement) {
    element.classList.add('pulse-urgent')
    return () => {
      element.classList.remove('pulse-urgent')
    }
  }

  return {
    animationContainer,
    createSuccessAnimation,
    createFailureAnimation,
    createFloatingHeart,
    addSuccessButtonEffect,
    addCountdownTickEffect,
    addUrgentPulse,
  }
}
